import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { CardSizes } from '@atlassian/cards-extension';

import { fg } from '@confluence/feature-gating';

import {
	MIN_GAP_BETWEEN_CARDS,
	MAX_GAP_BETWEEN_CARDS,
} from '../link-cards/LinkCardsContent/CardsOrientation';

import { getCardDimensions } from './CardOrientationWrapper';

export type useWidthAndSpacingCalculationsProps = {
	numberOfCards: number;
	cardsSize?: CardSizes;
	getItemDimensions?: () => CardDimensions;
};

export type GridGaps = {
	spaceBetweenCards: number;
	paddingRightGapOffset: number;
};

type CardDimensions = {
	minWidth: number;
	maxWidth: number;
	height: number;
};

type Calculations = {
	cardWidthOnFullRow: number;
	isFirstRowFull: boolean;
	isFirstRowOverflown: boolean;
	isGridSmallerOrEqualWidthToCards: boolean;
	gaps: GridGaps;
	maxNumberOfCardsPerRow: number;
};

type useWidthAndSpacingCalculationsResults = {
	calculations: Calculations;
	setCurrentCardsGridWidth: Dispatch<SetStateAction<number>>;
	setCurrentCardWidth: Dispatch<SetStateAction<number>>;
};

const MAX_NUMBER_OF_CARDS_PER_ROW = 6;

export const useWidthAndSpacingCalculations = ({
	numberOfCards,
	cardsSize,
	getItemDimensions,
}: useWidthAndSpacingCalculationsProps): useWidthAndSpacingCalculationsResults => {
	const [currentCardsGridWidth, setCurrentCardsGridWidth] = useState<number>(0);

	const [currentCardWidth, setCurrentCardWidth] = useState<number>(0);

	const cardDimensions = getItemDimensions
		? getItemDimensions()
		: getCardDimensions(cardsSize ?? CardSizes.MEDIUM, fg('company_hub_cards_visual_update'));

	/*
	 * Width observer rounds the width of the container (currentCardsGridWidth) returning a whole number.
	 * This means in situations that the width is rounded up, we have an edge case where we are
	 * making calculations with a grid width larger than what we actually have leading to layout issues.
	 *
	 * It is better to adjust the width we get from the observer down (-.5px) and
	 * have some cases where we have extra space (<=.5px) than the latter problem that causes layout issues.
	 */
	const currentCardsGridWidthAdjustedForRounding = currentCardsGridWidth - 0.5;

	const numberOfCardsPerRow = Math.floor(
		(currentCardsGridWidthAdjustedForRounding + MIN_GAP_BETWEEN_CARDS) /
			(cardDimensions.minWidth + MIN_GAP_BETWEEN_CARDS),
	);
	const maxNumberOfCardsPerRow = fg('company_hub_cards_visual_update')
		? Math.min(numberOfCardsPerRow, MAX_NUMBER_OF_CARDS_PER_ROW)
		: numberOfCardsPerRow;

	if (currentCardsGridWidthAdjustedForRounding <= cardDimensions.minWidth)
		return {
			calculations: {
				cardWidthOnFullRow: cardDimensions.minWidth,
				isFirstRowFull: true,
				isFirstRowOverflown: false,
				isGridSmallerOrEqualWidthToCards: true,
				gaps: {
					spaceBetweenCards: MIN_GAP_BETWEEN_CARDS,
					paddingRightGapOffset: 0,
				},
				maxNumberOfCardsPerRow,
			},
			setCurrentCardsGridWidth,
			setCurrentCardWidth,
		};

	const maxCardSizeOnFullRow = Math.floor(
		(currentCardsGridWidthAdjustedForRounding -
			MIN_GAP_BETWEEN_CARDS * (maxNumberOfCardsPerRow - 1)) /
			maxNumberOfCardsPerRow,
	);

	const isFirstRowFull = numberOfCards >= maxNumberOfCardsPerRow;

	const isFirstRowOverflown = numberOfCards > maxNumberOfCardsPerRow;

	const cardWidthOnFullRow = Math.min(maxCardSizeOnFullRow, cardDimensions.maxWidth);

	const cardsOnFirstRow = Math.min(numberOfCards, maxNumberOfCardsPerRow);

	const numberOfRowGaps = Math.max(0, cardsOnFirstRow - 1);

	const extraSpaceInGrid =
		currentCardsGridWidthAdjustedForRounding -
		cardsOnFirstRow * currentCardWidth -
		numberOfRowGaps * MIN_GAP_BETWEEN_CARDS;

	const maxAdditionalSpacePerGapBetweenCards =
		numberOfRowGaps === 0
			? 0
			: Math.min(extraSpaceInGrid / numberOfRowGaps, MAX_GAP_BETWEEN_CARDS - MIN_GAP_BETWEEN_CARDS);

	const spaceBetweenCards = MIN_GAP_BETWEEN_CARDS + maxAdditionalSpacePerGapBetweenCards;

	const paddingRightGapOffset = !isFirstRowFull
		? 0
		: (extraSpaceInGrid - numberOfRowGaps * maxAdditionalSpacePerGapBetweenCards) / 2;

	const calculations: Calculations = {
		cardWidthOnFullRow,
		isFirstRowFull,
		isFirstRowOverflown,
		isGridSmallerOrEqualWidthToCards: false,
		gaps: {
			spaceBetweenCards,
			paddingRightGapOffset,
		},
		maxNumberOfCardsPerRow,
	};

	return { calculations, setCurrentCardsGridWidth, setCurrentCardWidth };
};
